import React, { useEffect, useState } from 'react';
import { Landmark, Plus, Info, Star, Trash2, Loader2, X, Save, User, Hash, IdCard, Wallet, AlertCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { ContaBancaria } from '../../types';
import { getFunctionErrorMessage } from '../../src/lib/functionErrors';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';

const emptyForm = { banco: '', agencia: '', conta_corrente: '', tipo_conta: 'corrente' as 'corrente' | 'poupanca', titular_nome: '', titular_cpf_cnpj: '' };

const DadosBancariosView: React.FC = () => {
    const { user } = useAuth();
    const [contas, setContas] = useState<ContaBancaria[]>([]);
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const fetchContas = async () => {
        setLoading(true);
        setListError(null);
        try {
            const { data, error } = await supabase.functions.invoke('manage-bank-accounts', { body: { action: 'list' } });
            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || 'Erro ao buscar contas bancárias.');
            setContas(data.data);
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao buscar contas bancárias.');
            console.error('Erro ao buscar contas bancárias:', err);
            setListError(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContas(); }, []);

    const openModal = () => {
        setForm(emptyForm);
        setError(null);
        setShowModal(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const { data, error } = await supabase.functions.invoke('manage-bank-accounts', { body: { action: 'add', ...form } });
            if (error) throw error;
            if (data && data.success === false) throw new Error(data.error || 'Erro ao salvar a conta bancária.');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'dados_bancarios', acao: 'criar_conta_bancaria', origem: 'acao' });
            setShowModal(false);
            await fetchContas();
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao salvar a conta bancária.');
            console.error('Erro ao salvar conta bancária:', err);
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleSetPrincipal = async (id: string) => {
        setBusyId(id);
        setActionError(null);
        try {
            const { data, error } = await supabase.functions.invoke('manage-bank-accounts', { body: { action: 'setPrincipal', id } });
            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || 'Erro ao definir conta principal.');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'dados_bancarios', acao: 'definir_conta_bancaria_principal', origem: 'acao' });
            await fetchContas();
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao definir conta principal.');
            console.error('Erro ao definir conta principal:', err);
            setActionError(message);
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Excluir esta conta bancária?')) return;
        setBusyId(id);
        setActionError(null);
        try {
            const { data, error } = await supabase.functions.invoke('manage-bank-accounts', { body: { action: 'delete', id } });
            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || 'Erro ao excluir a conta bancária.');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'dados_bancarios', acao: 'excluir_conta_bancaria', origem: 'acao' });
            await fetchContas();
        } catch (err) {
            const message = await getFunctionErrorMessage(err, 'Erro ao excluir a conta bancária.');
            console.error('Erro ao excluir conta bancária:', err);
            setActionError(message);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="w-full bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 shrink-0 rounded-2xl bg-primary flex items-center justify-center text-white">
                        <Landmark size={26} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold font-poppins dark:text-white">Dados Bancários</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Gerencie suas contas para recebimento de bônus.</p>
                    </div>
                </div>
                <button
                    onClick={openModal}
                    className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                    <Plus size={18} /> Adicionar Conta
                </button>
            </div>

            {/* Aviso */}
            <div className="flex items-start gap-3 bg-primary/5 dark:bg-primary/10 border border-primary/10 dark:border-primary/20 rounded-2xl p-5">
                <div className="w-9 h-9 shrink-0 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-primary">
                    <Info size={18} />
                </div>
                <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Importante</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Seus bônus serão depositados na conta marcada como principal. Você pode cadastrar múltiplas contas e alternar entre elas a qualquer momento.
                    </p>
                </div>
            </div>

            {/* Lista de contas */}
            <div className="w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-8">
                {actionError && (
                    <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm mb-4">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        {actionError}
                    </div>
                )}
                {loading ? (
                    <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
                ) : listError ? (
                    <div className="flex flex-col items-center text-center py-8">
                        <AlertCircle size={40} className="text-red-300 dark:text-red-800 mb-3" />
                        <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1">Não foi possível carregar suas contas</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-sm">{listError}</p>
                        <button
                            onClick={fetchContas}
                            className="px-4 py-2 rounded-full text-sm font-bold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Tentar novamente
                        </button>
                    </div>
                ) : contas.length === 0 ? (
                    <div className="flex flex-col items-center text-center py-8">
                        <Landmark size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
                        <p className="text-base font-bold text-gray-700 dark:text-gray-200">Nenhuma conta cadastrada</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 mb-5">Adicione uma conta bancária para receber seus bônus.</p>
                        <button
                            onClick={openModal}
                            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                        >
                            <Plus size={18} /> Adicionar primeira conta
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {contas.map(conta => (
                            <div key={conta.id} className={`flex items-center justify-between gap-3 p-4 rounded-xl border ${conta.principal ? 'border-primary/30 bg-primary/5 dark:bg-primary/10' : 'border-gray-100 dark:border-gray-700'}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 shrink-0 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300">
                                        <Landmark size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">{conta.banco}</p>
                                            {conta.principal && (
                                                <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary text-white">
                                                    <Star size={10} fill="currentColor" /> Principal
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            Ag. {conta.agencia} · Conta {conta.conta_corrente} · {conta.tipo_conta === 'corrente' ? 'Corrente' : 'Poupança'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {!conta.principal && (
                                        <button
                                            onClick={() => handleSetPrincipal(conta.id)}
                                            disabled={busyId === conta.id}
                                            title="Marcar como principal"
                                            className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                                        >
                                            <Star size={16} />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(conta.id)}
                                        disabled={busyId === conta.id}
                                        title="Excluir conta"
                                        className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal Adicionar Conta */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-gray-900 dark:text-white">Adicionar Conta Bancária</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-5">
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Banco</label>
                                    <div className="relative">
                                        <Landmark size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text" required value={form.banco}
                                            onChange={(e) => setForm(f => ({ ...f, banco: e.target.value }))}
                                            placeholder="Ex: Nubank, Banco do Brasil"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Conta</label>
                                    <div className="relative">
                                        <Wallet size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <select
                                            value={form.tipo_conta}
                                            onChange={(e) => setForm(f => ({ ...f, tipo_conta: e.target.value as 'corrente' | 'poupanca' }))}
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        >
                                            <option value="corrente">Conta Corrente</option>
                                            <option value="poupanca">Conta Poupança</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agência</label>
                                    <div className="relative">
                                        <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text" required value={form.agencia}
                                            onChange={(e) => setForm(f => ({ ...f, agencia: e.target.value }))}
                                            placeholder="0000"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conta</label>
                                    <div className="relative">
                                        <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text" required value={form.conta_corrente}
                                            onChange={(e) => setForm(f => ({ ...f, conta_corrente: e.target.value }))}
                                            placeholder="00000-0"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titular</label>
                                    <div className="relative">
                                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text" required value={form.titular_nome}
                                            onChange={(e) => setForm(f => ({ ...f, titular_nome: e.target.value }))}
                                            placeholder="Nome completo do titular"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ do Titular</label>
                                    <div className="relative">
                                        <IdCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text" required value={form.titular_cpf_cnpj}
                                            onChange={(e) => setForm(f => ({ ...f, titular_cpf_cnpj: e.target.value }))}
                                            placeholder="000.000.000-00"
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-70"
                                >
                                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                    Salvar Conta
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DadosBancariosView;